import { useState, ComponentType, cloneElement, ReactElement } from "react";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaTheatre } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaTheatre";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import FixaCarousel from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import { ZoomableImage } from "./ZoomableImage";
import { useTranslation } from "@coworker/locales";
import { Image } from "../types/images";

type ZoomableImageProps = React.ComponentProps<typeof ZoomableImage>;

interface ImageViewerProps {
  images: Image[];
  isOpen: boolean;
  onClose: () => void;
  carouselId: string;
  ZoomableImageComponent?: ComponentType<ZoomableImageProps>;
  renderWrapper?: (imageElement: ReactElement, key: string) => ReactElement;
  slideIndex?: number;
}

const generateImageKey = (
  index: number,
  isVisible: boolean,
  currentIndex: number
) => {
  return `zoom-${index}-viewed-${isVisible ? currentIndex : "hidden"}`;
};

export const ImageViewer = ({
  images,
  isOpen,
  onClose,
  carouselId,
  ZoomableImageComponent = ZoomableImage,
  renderWrapper,
  slideIndex,
}: ImageViewerProps) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleVisibleSlideChange = (indices: number[]) => {
    if (indices.length > 0 && indices[0] !== undefined) {
      setCurrentIndex(indices[0]);
    }
  };

  if (!isOpen) return null;

  const renderImage = (image: Image, index: number) => {
    const rotation = image?.rotation ?? 0;
    const isVisible = currentIndex === index;
    const key = generateImageKey(index, isVisible, currentIndex);

    const imageElement = (
      <ZoomableImageComponent
        src={image.url}
        alt={image.fileName ?? `Image ${index + 1}`}
        rotation={rotation}
        fallback={!image.url || image.url.trim() === ""}
      />
    );

    return renderWrapper
      ? renderWrapper(imageElement, key)
      : cloneElement(imageElement, { key });
  };

  return (
    <FixaModal visible={isOpen} handleCloseBtn={onClose}>
      <FixaTheatre
        header={<FixaModalHeader />}
        ariaLabel={t("imageCarouselString")}
      >
        <FixaCarousel
          id={carouselId}
          data-testid={carouselId}
          isSlideShow
          hasNoControls={false}
          onVisibleSlideChange={handleVisibleSlideChange}
          ariaLabelLeftBtn={t("previousImageString")}
          ariaLabelRightBtn={t("nextImageString")}
          slideIndex={slideIndex}
        >
          {images.map(renderImage)}
        </FixaCarousel>
      </FixaTheatre>
    </FixaModal>
  );
};
